"use client";

import { useId } from "react";
import { isMovementActivityPostPayload } from "@/lib/movement/activity-post-payload";
import type { RunningTemplateData } from "../running-template";
import { MovementActivityDisplayPreview } from "./movement-activity-display-preview";
import { ActivityScenePreview } from "./reference-preview-primitives";

type RunningDisplayPreviewProps = {
  data: RunningTemplateData | null;
  participants: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }[];
};

/*
 * A floodlit stadium at dusk: the grandstand glows under two light towers, and
 * the red track sweeps out of the foreground towards a vanishing point on the
 * right — lane numbers, a start line and a set of starting blocks on it, with a
 * racing flat in the left foreground.
 *
 * One panoramic SVG (640×220, `slice`): a 301px feed card shows the composed
 * centre, a full-width post reveals more stadium either side. The bottom-centre
 * participant pill rides on open track. Motion is one compositor-only CSS loop
 * (the floodlight shimmer), off under reduced motion.
 */
const RUNNING_KEYFRAMES = `
@keyframes run-flood { 0%, 100% { opacity: 0.85 } 50% { opacity: 1 } }
.run-flood { animation: run-flood 4.5s ease-in-out infinite }
@media (prefers-reduced-motion: reduce) { .run-flood { animation: none } }
`;

// Vanishing point of the straight, on the horizon right of centre.
const VP = { x: 470, y: 94 };
// Lane lines where they cross the bottom edge (y = 220), outer → inner.
const LANE_FEET = [-560, -405, -250, -95, 60, 215, 370, 525, 680];

/** x of a lane line at height y (lines run from its foot up to the VP). */
const laneX = (foot: number, y: number) =>
  VP.x + (foot - VP.x) * ((y - VP.y) / (220 - VP.y));

export function RunningDisplayPreview({
  data,
  participants,
}: RunningDisplayPreviewProps) {
  const uid = useId().replace(/[^\w-]/g, "");
  const id = (name: string) => `run-${uid}-${name}`;
  const url = (name: string) => `url(#${id(name)})`;

  if (isMovementActivityPostPayload(data)) {
    return (
      <MovementActivityDisplayPreview
        activityId="running"
        label="Running"
        payload={data}
      />
    );
  }

  const trackOuter = LANE_FEET[0];
  const trackInner = LANE_FEET[LANE_FEET.length - 1];
  // Lane-number row and start line, a little above the participant pill.
  const numberY = 146;
  const startY = 134;

  return (
    <ActivityScenePreview
      label="Running"
      backgroundClassName="bg-[#0c1128]"
      participants={participants}
      scene={
        <>
          <style href="running-scene-keyframes" precedence="default">
            {RUNNING_KEYFRAMES}
          </style>
          <svg
            className="absolute inset-0 h-full w-full"
            viewBox="0 0 640 220"
            preserveAspectRatio="xMidYMid slice"
            aria-hidden="true"
            focusable="false"
          >
            <defs>
              <linearGradient id={id("sky")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#0a1030" />
                <stop offset="0.28" stopColor="#1d2a66" />
                <stop offset="0.42" stopColor="#5a4a8c" />
                <stop offset="0.5" stopColor="#ef8a68" />
                <stop offset="0.56" stopColor="#ffc38a" />
              </linearGradient>
              <linearGradient id={id("stand")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#1a1f45" />
                <stop offset="1" stopColor="#0c0f26" />
              </linearGradient>
              <pattern
                id={id("crowd")}
                width="7"
                height="5"
                patternUnits="userSpaceOnUse"
              >
                <rect
                  width="2.2"
                  height="1.6"
                  x="1"
                  y="1"
                  fill="#ffd9a8"
                  opacity="0.5"
                />
                <rect
                  width="1.6"
                  height="1.4"
                  x="4.6"
                  y="3"
                  fill="#8fb4ff"
                  opacity="0.35"
                />
              </pattern>
              <linearGradient id={id("track")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#e2704c" />
                <stop offset="0.35" stopColor="#c9482f" />
                <stop offset="1" stopColor="#8f2a1c" />
              </linearGradient>
              <pattern
                id={id("grain")}
                width="5"
                height="4"
                patternUnits="userSpaceOnUse"
              >
                <rect
                  width="1"
                  height="1"
                  x="1"
                  y="1"
                  fill="#000"
                  opacity="0.18"
                />
                <rect
                  width="1"
                  height="1"
                  x="3.5"
                  y="3"
                  fill="#fff"
                  opacity="0.08"
                />
              </pattern>
              <linearGradient id={id("grass")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#2f6a3a" />
                <stop offset="1" stopColor="#173d22" />
              </linearGradient>
              <radialGradient id={id("flare")}>
                <stop offset="0" stopColor="#fffbe8" stopOpacity="1" />
                <stop offset="0.18" stopColor="#fff2c4" stopOpacity="0.75" />
                <stop offset="0.5" stopColor="#ffe3a0" stopOpacity="0.18" />
                <stop offset="1" stopColor="#ffe3a0" stopOpacity="0" />
              </radialGradient>
              <linearGradient id={id("beam")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#fff4d0" stopOpacity="0.32" />
                <stop offset="1" stopColor="#fff4d0" stopOpacity="0" />
              </linearGradient>
              <radialGradient id={id("pool")}>
                <stop offset="0" stopColor="#ffe0b0" stopOpacity="0.32" />
                <stop offset="1" stopColor="#ffe0b0" stopOpacity="0" />
              </radialGradient>
              <linearGradient id={id("upper")} x1="0" x2="1" y1="0" y2="1">
                <stop offset="0" stopColor="#ff7a4a" />
                <stop offset="0.55" stopColor="#ff3d2e" />
                <stop offset="1" stopColor="#a8160f" />
              </linearGradient>
              <linearGradient id={id("foam")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#ffffff" />
                <stop offset="1" stopColor="#cfd3dc" />
              </linearGradient>
              <linearGradient id={id("pedal")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#3c8dff" />
                <stop offset="1" stopColor="#1849a8" />
              </linearGradient>
              <linearGradient id={id("steel")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#d9dde6" />
                <stop offset="1" stopColor="#6c7384" />
              </linearGradient>
            </defs>

            {/* Dusk sky with the sunset caught under the stand roof */}
            <rect width="640" height="110" fill={url("sky")} />

            {/* Grandstand: roof line, crowd lights, front wall */}
            <path
              d="M-10 58 Q140 46 320 52 T650 56 L650 98 L-10 98 Z"
              fill={url("stand")}
            />
            <path
              d="M-10 64 Q140 54 320 60 T650 63 L650 92 L-10 92 Z"
              fill={url("crowd")}
            />
            <path
              d="M-10 58 Q140 46 320 52 T650 56"
              fill="none"
              stroke="#8fa8ff"
              strokeOpacity="0.35"
              strokeWidth="1"
            />
            <rect x="-10" y="90" width="660" height="6" fill="#0a0c1d" />
            <rect
              x="-10"
              y="90"
              width="660"
              height="0.8"
              fill="#ffd2a0"
              opacity="0.4"
            />

            {/* Infield beyond the outer lane */}
            <rect x="-10" y="96" width="660" height="130" fill={url("grass")} />
            {[0, 1, 2, 3, 4].map((i) => (
              <rect
                key={i}
                x="-10"
                y={98 + i * 7}
                width="660"
                height="3.5"
                fill="#fff"
                opacity="0.035"
              />
            ))}

            {/* The track: a wedge from the foreground up to the vanishing point */}
            <polygon
              points={`${VP.x},${VP.y} ${trackOuter},220 ${trackInner},220`}
              fill={url("track")}
            />
            <polygon
              points={`${VP.x},${VP.y} ${trackOuter},220 ${trackInner},220`}
              fill={url("grain")}
            />
            {/* Kerb along the outer edge */}
            <line
              x1={VP.x}
              y1={VP.y}
              x2={trackOuter}
              y2="220"
              stroke="#f4f1ea"
              strokeWidth="2.4"
            />
            <line
              x1={VP.x}
              y1={VP.y}
              x2={trackInner}
              y2="220"
              stroke="#f4f1ea"
              strokeWidth="2.4"
            />
            {LANE_FEET.slice(1, -1).map((foot) => (
              <line
                key={foot}
                x1={VP.x}
                y1={VP.y}
                x2={foot}
                y2="220"
                stroke="#fff"
                strokeOpacity="0.82"
                strokeWidth="1.6"
              />
            ))}

            {/* Start line + staggered lane numbers */}
            <line
              x1={laneX(trackOuter, startY)}
              y1={startY}
              x2={laneX(trackInner, startY)}
              y2={startY}
              stroke="#fff"
              strokeWidth="2.2"
            />
            {LANE_FEET.slice(0, -1).map((foot, index) => {
              const next = LANE_FEET[index + 1];
              const cx = (laneX(foot, numberY) + laneX(next, numberY)) / 2;
              const laneWidth = laneX(next, numberY) - laneX(foot, numberY);
              return (
                <text
                  key={foot}
                  x={cx}
                  y={numberY + 6}
                  textAnchor="middle"
                  fontSize={Math.min(laneWidth * 0.32, 22)}
                  fontWeight="900"
                  fill="#fff"
                  fillOpacity="0.88"
                  transform={`translate(${cx} ${numberY}) scale(1 0.62) translate(${-cx} ${-numberY})`}
                  style={{ fontFamily: "inherit" }}
                >
                  {8 - index}
                </text>
              );
            })}

            {/* Floodlight pools on the track */}
            <ellipse cx="250" cy="150" rx="150" ry="34" fill={url("pool")} />
            <ellipse cx="430" cy="128" rx="110" ry="22" fill={url("pool")} />

            {/* Floodlight towers + beams */}
            <g className="run-flood">
              <polygon
                points="186,30 214,30 300,150 120,150"
                fill={url("beam")}
              />
              <polygon
                points="436,26 464,26 520,140 360,140"
                fill={url("beam")}
              />
            </g>
            {[
              { x: 200, top: 18, base: 96 },
              { x: 450, top: 14, base: 96 },
              { x: 40, top: 30, base: 92 },
              { x: 610, top: 28, base: 92 },
            ].map((tower) => (
              <g key={tower.x}>
                <rect
                  x={tower.x - 1.2}
                  y={tower.top + 8}
                  width="2.4"
                  height={tower.base - tower.top - 8}
                  fill="#0b0e22"
                />
                <rect
                  x={tower.x - 15}
                  y={tower.top - 2}
                  width="30"
                  height="12"
                  rx="1.5"
                  fill="#141832"
                />
                {[0, 1, 2, 3, 4].map((col) =>
                  [0, 1].map((row) => (
                    <rect
                      key={`${col}-${row}`}
                      x={tower.x - 13 + col * 5.4}
                      y={tower.top + row * 5}
                      width="4.2"
                      height="3.6"
                      rx="0.6"
                      fill="#fffbe6"
                    />
                  )),
                )}
                <circle
                  className="run-flood"
                  cx={tower.x}
                  cy={tower.top + 4}
                  r="42"
                  fill={url("flare")}
                />
              </g>
            ))}

            {/* Starting blocks, midground on the track */}
            <g transform="translate(398 112)">
              <ellipse
                cx="22"
                cy="23"
                rx="28"
                ry="3.4"
                fill="#000"
                opacity="0.35"
              />
              <rect
                x="-2"
                y="18"
                width="50"
                height="4"
                rx="1.5"
                fill={url("steel")}
              />
              <rect
                x="-2"
                y="18"
                width="50"
                height="1"
                fill="#fff"
                opacity="0.6"
              />
              {[
                { x: 6, h: 14 },
                { x: 28, h: 18 },
              ].map((pedal) => (
                <g key={pedal.x}>
                  <polygon
                    points={`${pedal.x},19 ${pedal.x + 4},${19 - pedal.h} ${pedal.x + 13},${19 - pedal.h + 3} ${pedal.x + 12},19`}
                    fill={url("pedal")}
                  />
                  <polygon
                    points={`${pedal.x + 4},${19 - pedal.h} ${pedal.x + 13},${19 - pedal.h + 3} ${pedal.x + 12.4},${19 - pedal.h + 5} ${pedal.x + 4.6},${19 - pedal.h + 2}`}
                    fill="#9cc6ff"
                    opacity="0.85"
                  />
                </g>
              ))}
            </g>

            {/* Racing flat, left foreground */}
            <g transform="translate(178 146) rotate(-4)">
              <ellipse
                cx="40"
                cy="39"
                rx="40"
                ry="4"
                fill="#000"
                opacity="0.45"
              />
              {/* Outsole + spikes */}
              <path d="M3 31 Q3 37 9 37 L72 37 Q79 37 80 31 Z" fill="#141418" />
              {[14, 26, 38, 50, 62].map((x) => (
                <rect
                  key={x}
                  x={x}
                  y="36.4"
                  width="3"
                  height="1.8"
                  rx="0.6"
                  fill="#3a3a44"
                />
              ))}
              {/* Foam midsole */}
              <path
                d="M2 23 Q1 31 4 32 L79 32 Q82 29 79 26 Q70 24 60 25 L8 23 Z"
                fill={url("foam")}
              />
              <path
                d="M8 28 L74 29"
                stroke="#ff3d2e"
                strokeWidth="1"
                opacity="0.7"
              />
              {/* Upper */}
              <path
                d="M4 24 Q1 13 7 8 Q13 4 21 7 L32 12 Q44 9 56 16 Q69 21 79 26 Q70 24 60 25 Z"
                fill={url("upper")}
              />
              {/* Collar opening */}
              <path d="M7 9 Q14 6 22 9 Q16 12 8 12 Z" fill="#3a0806" />
              {/* Swoosh */}
              <path
                d="M14 22 Q34 22 62 16 Q40 25 20 25 Q14 25 14 22 Z"
                fill="#fff"
                opacity="0.92"
              />
              {/* Laces */}
              {[0, 1, 2, 3].map((i) => (
                <line
                  key={i}
                  x1={30 + i * 6}
                  y1={11 + i * 1.6}
                  x2={34 + i * 6}
                  y2={14 + i * 1.6}
                  stroke="#fff"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                />
              ))}
              {/* Rim light from the floods */}
              <path
                d="M6 9 Q13 4 21 7 L32 12 Q44 9 56 16"
                fill="none"
                stroke="#ffe2b8"
                strokeWidth="1.1"
                strokeLinecap="round"
                opacity="0.85"
              />
              {/* Heel tab */}
              <path d="M2 14 Q0 20 3 24 L6 24 Q4 18 6 12 Z" fill="#1c1c22" />
            </g>
          </svg>
        </>
      }
      overlays={
        <div className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(ellipse_115%_125%_at_50%_42%,transparent_50%,rgba(4,6,20,0.55)_100%)]" />
      }
    />
  );
}
